import { useAuth0 } from "@auth0/auth0-react";
import { InformationCircleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import React, { useMemo, useState } from "react";

import { Button, Headline, Paragraph, SelectItem, Small } from "@fronterahealth/frontera-ui-components";

import {
  ApiProviderMetadataInvitationStatusChoices,
  ConnectionTypeEnum,
  LearnerType,
  ProviderRoleEnums,
  ProviderType,
  useCreateProviderMutation,
  useLearnersQuery,
  useReissueProviderInvitationMutation,
} from "@api/graphql/types-and-hooks";
import {
  CopyToClipboardButton,
  CopyToClipboardButtonVariant,
} from "@components/CopyToClipboardButton/CopyToClipboardButton";
import { FormContainer } from "@components/forms/FormLayout";
import { useFormUtils } from "@components/forms/useFormUtils";
import { notifyError, notifySuccess } from "@components/notifications/notifications";
import { MemberProvider } from "@pages/Members/types";
import { mapProviderToMemberProvider } from "@pages/Members/utils/providerMapping";
import { getRoleDescription, getRoleLabelFromEnum } from "@pages/Members/utils/providerRoles";
import { useAdminData } from "@providers/AdminDataProvider";
import { useProvidersData } from "@providers/ProvidersDataProvider";

interface InviteProviderModalProps {
  isOpen: boolean;
  onClose: () => void;
  preselectedProviderId?: string;
}

enum Step {
  Select = "select",
  Manual = "manual",
  Confirm = "confirm",
}

enum ConfirmMode {
  Existing = "existing",
  Manual = "manual",
}

const ASSIGNABLE_ROLES = Object.values(ProviderRoleEnums).filter((role) => role !== ProviderRoleEnums.Intake);

interface ManualFormData {
  firstName: string;
  lastName: string;
  email: string;
  providerRoles: ProviderRoleEnums[];
  connectionType: ConnectionTypeEnum;
  learners: SelectItem[];
}

const EMPTY_MANUAL_FORM: ManualFormData = {
  firstName: "",
  lastName: "",
  email: "",
  providerRoles: [],
  connectionType: ConnectionTypeEnum.UsernamePassword,
  learners: [],
};

const AddManuallyPrompt: React.FC<{ isPassageHealthUser: boolean; onAddManually: () => void }> = ({
  isPassageHealthUser,
  onAddManually,
}) => {
  if (isPassageHealthUser) {
    return (
      <div className="flex items-start gap-2 rounded-md border border-limestone-200 bg-limestone-50 p-3">
        <InformationCircleIcon className="h-5 w-5 shrink-0 text-icon-tertiary" />
        <Paragraph className="text-sm" colorType="secondary">
          Can&apos;t find them? Providers are managed through Passage — add or update them there.
        </Paragraph>
      </div>
    );
  }
  return <Button variant="primary" buttonStyle="ghost" text="Can't find them? Add manually" onClick={onAddManually} />;
};

export const InviteProviderModal: React.FC<InviteProviderModalProps> = ({ isOpen, onClose, preselectedProviderId }) => {
  const { providersQuery } = useProvidersData();
  const { isAuthenticated } = useAuth0();
  const { roles, isPassageHealthUser } = useAdminData();
  const [step, setStep] = useState<Step>(Step.Select);
  const [confirmMode, setConfirmMode] = useState<ConfirmMode>(ConfirmMode.Existing);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedProvider, setSelectedProvider] = useState<MemberProvider | null>(null);

  const learnersQuery = useLearnersQuery(
    {},
    {
      queryKey: ["learners", isAuthenticated, JSON.stringify(roles)],
      retry: false,
      enabled:
        isAuthenticated &&
        !!roles.length &&
        (roles.includes("admin") || roles.includes("bcba") || roles.includes("assessment-bcba")),
      refetchOnWindowFocus: false,
    },
  );

  const learners = (learnersQuery?.data?.learner || []).filter(Boolean) as LearnerType[];

  const learnerItems = useMemo(
    () =>
      learners
        .map((l) => ({
          id: l.id,
          primary: `${l.learnermetadata?.firstName} ${l.learnermetadata?.lastName}`,
        }))
        .sort((a, b) => (a.primary || "").localeCompare(b.primary || "")) as SelectItem[],
    [learners],
  );

  const providers = useMemo(
    () =>
      ((providersQuery.data?.provider || []) as ProviderType[])
        .map(mapProviderToMemberProvider)
        .filter((p): p is MemberProvider => p !== null),
    [providersQuery.data],
  );

  const createProvider = useCreateProviderMutation({
    onSuccess: (res) => {
      if (!res.createProvider?.status) {
        notifyError(res.createProvider?.message || "Something went wrong inviting this provider!");
        return;
      }
      notifySuccess(`Invite sent to ${res.createProvider.provider?.providermetadata?.firstName ?? "the provider"}`);
      providersQuery.refetch();
      onClose();
    },
    onError: () => {
      notifyError("Something went wrong inviting this provider!");
    },
  });

  const reissueInvitation = useReissueProviderInvitationMutation({
    onSuccess: (res) => {
      if (!res.reissueProviderInvitation?.status) {
        notifyError(res.reissueProviderInvitation?.message || "Something went wrong sending this invite!");
        return;
      }
      notifySuccess(`Invite sent to ${selectedProvider?.name}`);
      providersQuery.refetch();
      onClose();
    },
    onError: () => {
      notifyError("Something went wrong sending this invite!");
    },
  });

  const isSending = createProvider.isPending || reissueInvitation.isPending;

  const {
    formState,
    onSubmit,
    reset,
    watch,
    trigger,
    getValues,
    RegisteredFormInput,
    RegisteredCheckboxList,
    RegisteredFormRadioInput,
    RegisteredFormMultiSelect,
  } = useFormUtils<ManualFormData>({
    useNativeValidation: false,
    defaultValues: EMPTY_MANUAL_FORM,
    mutationFn: async () => {
      if (confirmMode === ConfirmMode.Existing) {
        if (!selectedProvider) return;
        await reissueInvitation.mutateAsync({ providerId: selectedProvider.id });
      } else {
        const values = getValues();
        await createProvider.mutateAsync({
          provider: {
            firstName: values.firstName,
            lastName: values.lastName,
            email: values.email,
            providerRole: values.providerRoles,
            connectionType: values.connectionType,
            learners: values.learners.filter((c) => !!c.id).map((c) => c.id!),
          },
        });
      }
    },
  });

  const [watchedFirstName, watchedLastName, watchedEmail] = watch(["firstName", "lastName", "email"]);

  // Reset state on open, or jump straight to confirm if preselectedProviderId resolves.
  const [prevOpenSyncInputs, setPrevOpenSyncInputs] = useState({ isOpen, preselectedProviderId, providers });
  if (
    isOpen !== prevOpenSyncInputs.isOpen ||
    preselectedProviderId !== prevOpenSyncInputs.preselectedProviderId ||
    providers !== prevOpenSyncInputs.providers
  ) {
    setPrevOpenSyncInputs({ isOpen, preselectedProviderId, providers });
    if (isOpen) {
      const provider = preselectedProviderId ? providers.find((p) => p.id === preselectedProviderId) : undefined;
      if (provider) {
        setSelectedProvider(provider);
        setConfirmMode(ConfirmMode.Existing);
        setStep(Step.Confirm);
      } else {
        setStep(Step.Select);
        setSearchQuery("");
        setSelectedProvider(null);
        reset(EMPTY_MANUAL_FORM);
      }
    }
  }

  const filteredProviders = useMemo(() => {
    if (!searchQuery.trim()) return providers;
    const q = searchQuery.toLowerCase();
    return providers.filter((p) => p.name.toLowerCase().includes(q) || p.email.toLowerCase().includes(q));
  }, [providers, searchQuery]);

  const isInvitable = (status: ApiProviderMetadataInvitationStatusChoices | null) =>
    status !== ApiProviderMetadataInvitationStatusChoices.Accepted;

  const statusLabel = (status: ApiProviderMetadataInvitationStatusChoices | null) => {
    switch (status) {
      case ApiProviderMetadataInvitationStatusChoices.Accepted:
        return "Active";
      case ApiProviderMetadataInvitationStatusChoices.Pending:
      case ApiProviderMetadataInvitationStatusChoices.Expired:
        return "Invited";
      case null:
        return null;
    }
  };

  const handleSelectProvider = (provider: MemberProvider) => {
    setSelectedProvider(provider);
    setConfirmMode(ConfirmMode.Existing);
    setStep(Step.Confirm);
  };

  const handleManualNext = async () => {
    const valid = await trigger(["firstName", "lastName", "email"]);
    if (!valid) return;
    const values = getValues();
    setSelectedProvider({
      id: "",
      firstName: values.firstName,
      lastName: values.lastName,
      name: `${values.firstName} ${values.lastName}`,
      email: values.email,
      roles: values.providerRoles.map((role) => getRoleLabelFromEnum(role)) as MemberProvider["roles"],
      assignedClientsCount: values.learners.length,
      portalStatus: null,
      clients: [],
    });
    setConfirmMode(ConfirmMode.Manual);
    setStep(Step.Confirm);
  };

  const handleBack = () => {
    if (step === Step.Confirm && !preselectedProviderId) {
      setStep(confirmMode === ConfirmMode.Manual ? Step.Manual : Step.Select);
    } else if (step === Step.Manual) {
      setStep(Step.Select);
    } else {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <FormContainer onSubmit={onSubmit} className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
        {/* Header */}
        <div className="mb-4 flex items-center justify-between">
          <Headline as="h2" size="small">
            {step === Step.Select && "Invite a provider"}
            {step === Step.Manual && "Add provider manually"}
            {step === Step.Confirm && "Confirm invite"}
          </Headline>
          <button
            onClick={onClose}
            className="cursor-pointer text-text-secondary hover:text-text-primary"
            aria-label="Close"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Step 1: Select provider */}
        {step === Step.Select && (
          <div className="flex flex-col gap-4">
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="block w-full rounded-md border border-limestone-200 px-3 py-2 text-sm text-text-primary placeholder:text-text-secondary focus:border-rio-600 focus:outline-none focus:ring-1 focus:ring-rio-600"
            />

            <div className="max-h-64 overflow-y-auto [&::-webkit-scrollbar]:hidden" style={{ scrollbarWidth: "none" }}>
              {filteredProviders.length === 0 ? (
                <Paragraph colorType="secondary" className="py-4 text-center">
                  No providers found
                </Paragraph>
              ) : (
                <ul className="divide-y divide-limestone-100">
                  {filteredProviders.map((provider) => {
                    const canInvite = isInvitable(provider.portalStatus);
                    const label = statusLabel(provider.portalStatus);
                    return (
                      <li key={provider.id}>
                        <button
                          type="button"
                          onClick={() => canInvite && handleSelectProvider(provider)}
                          disabled={!canInvite}
                          className={
                            canInvite
                              ? "flex w-full items-center gap-3 px-2 py-3 text-left hover:bg-limestone-50"
                              : "flex w-full cursor-default items-center gap-3 px-2 py-3 text-left opacity-50"
                          }
                        >
                          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-globemallow-100 text-sm font-medium text-globemallow-700">
                            {provider.firstName[0]}
                            {provider.lastName[0]}
                          </div>
                          <div className="min-w-0 flex-1">
                            <Small displayType="loud" colorType="primary">
                              {provider.name}
                            </Small>
                            <Small colorType="secondary" className="text-xs">
                              {[provider.roles.join(", "), provider.email].filter(Boolean).join(" · ")}
                            </Small>
                          </div>
                          {label && <span className="shrink-0 text-xs font-medium text-text-secondary">{label}</span>}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <AddManuallyPrompt isPassageHealthUser={isPassageHealthUser} onAddManually={() => setStep(Step.Manual)} />
          </div>
        )}

        {/* Step 1b: Manual add */}
        {step === Step.Manual && (
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-3">
              <RegisteredFormInput formKey="firstName" formState={formState} label="First Name" />
              <RegisteredFormInput formKey="lastName" formState={formState} label="Last Name" />
            </div>

            <RegisteredFormInput formKey="email" formState={formState} label="Email" type="email" />

            <RegisteredCheckboxList
              formState={formState}
              formKey="providerRoles"
              title=""
              orientation="horizontal"
              items={ASSIGNABLE_ROLES.map((role) => ({
                id: role,
                title: getRoleLabelFromEnum(role),
                tooltipText: getRoleDescription(role),
                defaultChecked: getValues("providerRoles").includes(role),
              }))}
            />

            <RegisteredFormRadioInput
              formState={formState}
              formKey="connectionType"
              title="Auth0 Account Type"
              items={[
                { id: ConnectionTypeEnum.UsernamePassword, title: "Username & Password" },
                { id: ConnectionTypeEnum.GoogleOauth, title: "Google OAuth" },
              ]}
            />

            <RegisteredFormMultiSelect
              formState={formState}
              formKey="learners"
              title="Client Mappings"
              placeholderText="Search available clients"
              items={learnerItems}
              mode="tags"
              required={false}
              isLoading={learnersQuery.isFetching}
            />

            <div className="flex justify-end gap-3 pt-2">
              <Button variant="primary" buttonStyle="border" text="Back" onClick={() => setStep(Step.Select)} />
              <Button
                variant="primary"
                buttonStyle="filled"
                text="Next"
                onClick={handleManualNext}
                disabled={!watchedFirstName || !watchedLastName || !watchedEmail}
              />
            </div>
          </div>
        )}

        {/* Step 2: Confirm & send */}
        {step === Step.Confirm && selectedProvider && (
          <div className="flex flex-col gap-4">
            <div className="rounded-md border border-limestone-200 bg-limestone-50 p-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-globemallow-100 text-sm font-medium text-globemallow-700">
                  {selectedProvider.firstName[0]}
                  {selectedProvider.lastName[0]}
                </div>
                <div>
                  <Small displayType="loud" colorType="primary">
                    {selectedProvider.name}
                  </Small>
                  <Small colorType="secondary" className="text-xs">
                    {selectedProvider.email || "No email on file"}
                  </Small>
                  <Small colorType="secondary" className="text-xs">
                    {selectedProvider.roles.join(", ")}
                  </Small>
                </div>
              </div>
            </div>

            <div className="rounded-md border border-blue-200 bg-blue-50 p-3">
              <Paragraph className="text-sm text-blue-800">
                {selectedProvider.portalStatus === ApiProviderMetadataInvitationStatusChoices.Pending ||
                selectedProvider.portalStatus === ApiProviderMetadataInvitationStatusChoices.Expired ? (
                  <>
                    A new invite email will be resent to{" "}
                    <span className="font-medium">{selectedProvider.email || "the provider"}</span>.
                  </>
                ) : (
                  <>
                    An invite email will be sent to{" "}
                    <span className="font-medium">{selectedProvider.email || "the provider"}</span>. They&apos;ll be
                    asked to create a password and set up their account.
                  </>
                )}
              </Paragraph>
            </div>

            <div className="flex items-center justify-between pt-2">
              <div>
                <Button variant="primary" buttonStyle="border" text="Back" onClick={handleBack} disabled={isSending} />
              </div>
              <div className="flex items-baseline gap-3">
                <CopyToClipboardButton
                  text={selectedProvider.invitationUrl || ""}
                  variant={CopyToClipboardButtonVariant.Text}
                  label="Copy Invite Link"
                  className="mt-2"
                />
                <Button
                  type="submit"
                  variant="primary"
                  buttonStyle="filled"
                  text={
                    selectedProvider.portalStatus === ApiProviderMetadataInvitationStatusChoices.Pending ||
                    selectedProvider.portalStatus === ApiProviderMetadataInvitationStatusChoices.Expired
                      ? "Resend invite"
                      : "Send invite"
                  }
                  disabled={isSending}
                />
              </div>
            </div>
          </div>
        )}
      </FormContainer>
    </div>
  );
};
